/**
 * 地址栏 ⇄ 当前房间。整个应用只有这一处碰 `history`。
 *
 * 两件事：
 *
 *  1. **进来时读一次**（`takeRoomCode`）：`/r/K7M2P9`、`/join?room=rm_x&code=K7M2P9`
 *     以及裸 `?code=` 都能进房。读完**立刻**把地址抹成 `/`——
 *     不抹的话，玩家从牌桌退回大厅的那一刻大厅会再读到同一个码，把他又弹进房间，
 *     形成一个按返回键都出不来的循环（这正是 `consumeInviteCode` 想解决的问题，
 *     但它对 `/r/CODE` 这种**路径式**链接抹不掉，见 deviations）。
 *
 *  2. **在房间里就写进地址**（`startRoomRouteSync`）：刷新页面后还能凭地址回到同一个房间。
 *     用 `replaceState` 而不是 `pushState`：房间码不是一条「历史记录」，
 *     往返回栈里塞一条只会让系统返回键退回一个假页面。
 *
 * ★ 抹地址必须发生在 React 渲染大厅**之前**（AppRoot 的首次渲染体内），
 *   否则 `Lobby` 挂载时的 `consumeInviteCode` 会先看到它，两处各消费一次。
 */
import { parseInviteTarget } from '../lobby/roomCode.js'
import { useNetStore } from '../transport/applyMessage.js'

/** 分享链接的路径前缀：`/r/K7M2P9`。够短，念给人听也不费劲。 */
export const ROOM_PATH_PREFIX = '/r/'

export interface HistoryLike {
  replaceState(data: unknown, unused: string, url?: string): void
}

function currentHref(): string {
  return (globalThis as { location?: { href?: string } }).location?.href ?? ''
}

function currentHistory(): HistoryLike | null {
  return (globalThis as { history?: HistoryLike }).history ?? null
}

export function roomPath(code: string): string {
  return `${ROOM_PATH_PREFIX}${code}`
}

/**
 * 从一个**绝对** href 里认出房间码。认不出返回 null。
 *
 * `parseInviteTarget` 已经同时覆盖 `?code=` 与「路径末段」两种写法，
 * 所以 `/r/K7M2P9` 与 `/join?room=rm_x&code=K7M2P9` 都能被它认出来。
 */
export function roomCodeFromHref(href: string): string | null {
  return parseInviteTarget(href).code
}

/** 已经消费过一次的标记。第二次调用返回 null，绝不重复进房。 */
let consumed = false

/**
 * 读走地址里的房间码并把地址抹成 `/`。整个进程只生效一次。
 *
 * @returns 6 位房间码；地址里没有就是 null
 */
export function takeRoomCode(href: string = currentHref(), history: HistoryLike | null = currentHistory()): string | null {
  if (consumed) return null
  consumed = true
  const code = roomCodeFromHref(href)
  if (code === null) return null
  clearRoomRoute(history)
  return code
}

/** 测试用：把「只消费一次」的闸门复位。 */
export function resetRouteConsumption(): void {
  consumed = false
}

export function writeRoomRoute(code: string | null, history: HistoryLike | null = currentHistory()): void {
  if (history === null) return
  try {
    history.replaceState(null, '', code === null ? '/' : roomPath(code))
  } catch {
    // 某些 WebView 会对 replaceState 限流；地址栏不对不值得让牌桌崩掉
  }
}

export function clearRoomRoute(history: HistoryLike | null = currentHistory()): void {
  writeRoomRoute(null, history)
}

/**
 * 把「当前在哪个房间」持续写进地址栏。返回退订函数。
 *
 * 只在房间码**变化**时写：牌局中每秒好几帧，每帧都调 `replaceState`
 * 会被 Safari 判成滥用（100 次 / 30 秒的限流），之后连正常的导航都写不进去。
 */
export function startRoomRouteSync(history: HistoryLike | null = currentHistory()): () => void {
  let last: string | null | undefined
  const push = (code: string | null): void => {
    if (code === last) return
    last = code
    writeRoomRoute(code, history)
  }
  push(useNetStore.getState().view?.room.code ?? null)
  return useNetStore.subscribe((s) => {
    push(s.view?.room.code ?? null)
  })
}
